import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import GlobeCanvas from "../components/GlobeCanvas";
import { SiteHeader, SiteFooter, Icon } from "../components/SiteChrome";
import LocationSearch from "../components/LocationSearch";
import SavedPlaces from "../components/SavedPlaces";
import WeatherShowcase from "../components/WeatherShowcase";
import EarthComparison from "../components/EarthComparison";
import useFavorites from "../hooks/useFavorites";
import { EXAMPLE_LOCATIONS, monitorPath } from "../lib/locations";

const perspectives = [
  {
    id: "air",
    title: "The air we breathe.",
    name: "Air quality",
    source: "OPENWEATHER · CAMS",
    desc: "Explore fine particles and other pollutants, with current estimates and a forecast of the air ahead.",
    note: "Provider indices and models are shown separately.",
    metrics: ["PM₂.₅ & PM₁₀", "Air-quality indices", "Pollutant forecasts"],
  },
  {
    id: "climate",
    title: "A view of the week ahead.",
    name: "Weather",
    source: "OPEN-METEO",
    desc: "Follow temperature, rain and wind from current modelled conditions through a seven-day outlook.",
    note: "Weather models represent an area, rather than a sensor at every point.",
    metrics: ["Temperature", "Rain & wind", "Seven-day outlook"],
  },
  {
    id: "flood",
    title: "Water in motion.",
    name: "River flow",
    source: "GLOFAS VIA OPEN-METEO",
    desc: "See how modelled river discharge changes through recent history and a fourteen-day forecast.",
    note: "River grids and ensemble ranges provide context, not local flood warnings.",
    metrics: ["River discharge", "14-day outlook", "Forecast uncertainty"],
  },
  {
    id: "veg",
    title: "Beneath the surface.",
    name: "Soil & water",
    source: "OPEN-METEO",
    desc: "Connect shallow soil moisture, recent rain and atmospheric water demand over seven complete days.",
    note: "These are soil and weather models; this module does not measure satellite vegetation indices.",
    metrics: ["Shallow soil moisture", "Recent rainfall", "Water demand"],
  },
  {
    id: "drought",
    title: "The longer water story.",
    name: "Drought context",
    source: "HISTORICAL WEATHER",
    desc: "Put recent rainfall in perspective with thirty- and ninety-day records of climatic water balance.",
    note: "Historical context does not constitute a validated drought-severity classification.",
    metrics: ["30-day rainfall", "90-day balance", "Data coverage"],
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [globePaused, setGlobePaused] = useState(false);
  const [active, setActive] = useState(0);
  const { favorites, toggle, message } = useFavorites();
  const explore = (place) => navigate(monitorPath(place));
  const perspective = perspectives[active];
  return (
    <div className="site-page observatory-page">
      <SiteHeader />
      <main id="main-content">
        <section className="orbit-hero">
          <div className="site-container orbit-hero-inner">
            <div className="orbit-copy">
              <p className="orbit-eyebrow">
                <span /> YOUR ENVIRONMENT, IN PERSPECTIVE
              </p>
              <h1>
                One planet.
                <br />A <em>closer look.</em>
              </h1>
              <p className="orbit-lead">
                From the air above you to the ground beneath. Explore the
                environmental signals that shape a place.
              </p>
              <div className="orbit-search">
                <LocationSearch onSelect={explore} />
                <div className="orbit-examples">
                  <span>Start somewhere</span>
                  {[
                    EXAMPLE_LOCATIONS[0],
                    EXAMPLE_LOCATIONS[3],
                    EXAMPLE_LOCATIONS[7],
                  ].map((place) => (
                    <Link key={place.name} to={monitorPath(place)}>
                      {place.name} <span aria-hidden="true">↗</span>
                    </Link>
                  ))}
                </div>
                <SavedPlaces
                  places={favorites}
                  onSelect={explore}
                  onRemove={toggle}
                />
                {message && <p role="status">{message}</p>}
              </div>
            </div>
            <div className="orbit-earth">
              <div className="orbit-earth-top">
                <span>EARTH / GLOBAL PERSPECTIVE</span>
                <Icon name="globe" size={18} />
              </div>
              <GlobeCanvas paused={globePaused} />
              <div className="orbit-earth-bottom">
                <span>Illustrated Earth · Open data, local context</span>
                <button
                  type="button"
                  aria-pressed={globePaused}
                  onClick={() => setGlobePaused(!globePaused)}
                >
                  {globePaused ? "Resume globe" : "Pause globe"}
                  <span aria-hidden="true">{globePaused ? " ▷" : " Ⅱ"}</span>
                </button>
              </div>
            </div>
          </div>
          <div className="site-container orbit-hero-bottom">
            <span>ATMOSPHERE / WEATHER / LAND / WATER</span>
            <a href="#explore">
              Explore the perspectives <span aria-hidden="true">↓</span>
            </a>
          </div>
        </section>
        <section
          className="orbit-providers"
          aria-label="Environmental data providers"
        >
          <div className="site-container">
            <span>DATA FROM</span>
            <b>OpenWeather</b>
            <b>Open-Meteo</b>
            <b>
              Copernicus <small>CAMS</small>
            </b>
            <b>GloFAS</b>
            <Link to="/modules#data">View sources ↗</Link>
          </div>
        </section>
        <section className="orbit-weather site-container" id="explore">
          <div className="orbit-section-heading">
            <div>
              <p className="eyebrow">01 / START WITH A PLACE</p>
              <h2>
                A world of data.
                <br />
                Made <em>local.</em>
              </h2>
            </div>
            <p>
              Select a city. See its weather take shape.
              <br />
              Then open the explorer for the full picture.
            </p>
          </div>
          <WeatherShowcase />
        </section>
        <section className="orbit-perspectives" id="modules">
          <div className="site-container">
            <div className="orbit-section-heading">
              <div>
                <p className="eyebrow">02 / CONNECT THE SIGNALS</p>
                <h2>
                  Five perspectives.
                  <br />
                  One connected view.
                </h2>
              </div>
              <Link className="orbit-text-link" to="/modules">
                Explore the data & methods <Icon name="arrow" size={20} />
              </Link>
            </div>
            <div className="perspective-layout">
              <div
                className="perspective-selector"
                role="group"
                aria-label="Environmental perspectives"
              >
                {perspectives.map((item, i) => (
                  <button
                    key={item.id}
                    type="button"
                    aria-pressed={active === i}
                    onClick={() => setActive(i)}
                    aria-controls="perspective-detail"
                  >
                    <span className="perspective-number">0{i + 1}</span>
                    <Icon name={item.id} size={22} />
                    <span>{item.name}</span>
                    <span className="perspective-arrow" aria-hidden="true">
                      ↗
                    </span>
                  </button>
                ))}
              </div>
              <article
                className="perspective-detail"
                id="perspective-detail"
                aria-live="polite"
              >
                <div className="perspective-detail-top">
                  <span>{perspective.source}</span>
                  <Icon name={perspective.id} size={48} />
                </div>
                <h3>{perspective.title}</h3>
                <p>{perspective.desc}</p>
                <ul>
                  {perspective.metrics.map((metric) => (
                    <li key={metric}>{metric}</li>
                  ))}
                </ul>
                <Link
                  className="orbit-button"
                  to={`/monitor?module=${perspective.id}`}
                >
                  Explore {perspective.name.toLowerCase()}{" "}
                  <Icon name="arrow" size={19} />
                </Link>
                <small>{perspective.note}</small>
              </article>
            </div>
          </div>
        </section>
        <EarthComparison />
        <section className="orbit-principles site-container">
          <div>
            <p className="eyebrow">04 / KNOW WHAT YOU’RE SEEING</p>
            <h2>
              Clarity goes
              <br />
              beyond the numbers.
            </h2>
            <Link className="orbit-text-link" to="/modules#data">
              Our sources & methodology <Icon name="arrow" size={20} />
            </Link>
          </div>
          <div className="orbit-principle-list">
            {[
              [
                "01",
                "The source stays in sight.",
                "Provider names, model context and data timestamps travel with the result.",
              ],
              [
                "02",
                "Uncertainty has a place.",
                "Estimates, forecasts and historical records are labelled. Missing data stays missing.",
              ],
              [
                "03",
                "Your place, ready to revisit.",
                "Save places in your browser or share a link to the same location and module. No account needed.",
              ],
            ].map(([n, title, desc]) => (
              <article key={n}>
                <span>{n}</span>
                <div>
                  <h3>{title}</h3>
                  <p>{desc}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
        <section className="orbit-invitation">
          <div className="site-container">
            <p className="orbit-eyebrow">
              THE BIG PICTURE STARTS WITH YOUR PLACE.
            </p>
            <h2>
              What’s happening
              <br />
              <em>around you?</em>
            </h2>
            <Link className="orbit-button" to="/monitor">
              Launch the explorer <Icon name="arrow" size={22} />
            </Link>
            <p>Global coordinates. Five perspectives. Open to explore.</p>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
